import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { CalendarPlus, FileDown } from 'lucide-react';
import { phaseStandings, overallStandings, placementTier, effectiveRules, phaseRanking, clubStandings, playerStandings, teamAggregates, type GroupsConfig } from '@airmail/engine';
import { useCompetition, useSync, useTurnier } from '@/store/TournamentProvider';
import { Button, Card, Empty, SectionHead } from '@/ui';
import { useT } from '@/i18n';
import { StandingsTable } from '@/components/StandingsTable';
import { GesamtTabelle } from '@/components/GesamtTabelle';
import { AggregateTable } from '@/components/AggregateTable';
import { SeasonImportDialog } from '@/components/SeasonImportDialog';
import { teamLookup } from '@/lib/teams';
import { isClubMode } from '@/lib/modes';
import { dayFromTournament, listSeasons, spieltagFileName } from '@/store/season';
import { downloadText } from '@/lib/download';
import { Gate } from '@/components/ProMarke';

export function TabellePage() {
  const t = useT();
  const { tournament } = useTurnier();
  const sync = useSync();
  /*
   * Der Helfer sieht die Tabelle, darf aber nichts daran hängen, was das
   * Turnier verlässt: keine Saison anlegen, keinen Spieltag übernehmen, keine
   * Datei exportieren (Harald, 09/2026). Das ist wie der Rest des
   * Helfer-Modus ein Schutz vor Versehen, keine Schranke am Server
   * (docs/notes/helfer.md).
   */
  const helfer = sync.cfg?.role === 'helfer';
  const comp = useCompetition();
  const lookup = teamLookup(comp);
  const groupsPhase = comp.phases.find((p) => p.type === 'groups');
  // KO-Baum oder von Hand gestartete KO-Runden (docs/notes/ko-runden.md) — beide liefern einen Endstand
  const koPhase = comp.phases.find((p) => (p.type === 'single_elim' || p.type === 'ko_rounds') && p.status !== 'planned');
  // Leere Gruppen (alle Spieler in andere verschoben) nicht zeigen — Daniel, 10/2026
  const tables = groupsPhase ? phaseStandings(tournament, groupsPhase.id).filter((x) => x.group.teamIds.length > 0) : [];
  const cfg = groupsPhase?.config as GroupsConfig | undefined;
  const clubMode = isClubMode(comp.phases.map((p) => p.config));
  const [seasonOpen, setSeasonOpen] = useState(false);
  /*
   * Spieltag fertig, aber noch in keiner Saison (Liga-Modus): „In Saison
   * übernehmen" wird hervorgehoben, bis der Spieltag drin ist — sonst geht er
   * unter, und die Saison stimmt nicht mehr. Ob er drin ist, sagen die lokalen
   * Saisons (die Spieltage tragen die Turnier-ID); `null` = noch nicht
   * nachgesehen, dann lieber nichts hervorheben als falsch.
   */
  const spieltagFertig = clubMode && !helfer && (groupsPhase?.status === 'finished' || tournament.status === 'finished');
  const [inSaison, setInSaison] = useState<boolean | null>(null);
  useEffect(() => {
    if (!spieltagFertig) return;
    let aktuell = true;
    listSeasons().then(
      (seasons) => aktuell && setInSaison(seasons.some((s) => s.days.some((d) => d.tournamentId === tournament.id))),
      () => aktuell && setInSaison(false),
    );
    return () => {
      aktuell = false;
    };
  }, [spieltagFertig, tournament.id]);
  const hervorheben = spieltagFertig && inSaison === false;
  /** Spieltag-Datei (docs/17): Tabellen dieses Turniers für die Saison einer anderen Mannschaft — ohne Internet */
  const exportDay = () => {
    const day = dayFromTournament(tournament);
    if (!day) return;
    downloadText(spieltagFileName(tournament), JSON.stringify({ kind: 'airmail-spieltag', version: 1, exportedAt: new Date().toISOString(), day }));
  };

  // Beste Nächstplatzierte: dieselbe Stufe, die die Engine für die Setzung nimmt (standings/placement.ts)
  const bestRemaining = groupsPhase && cfg && cfg.bestRemaining > 0 ? placementTier(tables, effectiveRules(comp.ruleSet, groupsPhase.ruleSetOverride), cfg.qualifiersPerGroup + 1) : [];
  const finalRanking = koPhase ? phaseRanking(koPhase) : [];
  // Gesamttabelle über alle Gruppen (Harald): Punkte = Siege − Niederlagen, dann S, N, Wurfdifferenz
  const gesamt = groupsPhase ? overallStandings(tournament, groupsPhase.id) : [];
  const allRows = tables.flatMap((t) => t.rows);
  const pending = (groupId: string) => groupsPhase?.matches.some((m) => m.groupId === groupId && (m.status === 'ready' || m.status === 'running' || m.status === 'open')) ?? false;
  const done = groupsPhase ? groupsPhase.matches.filter((m) => m.status === 'finished' || m.status === 'walkover').length : 0;
  const meta = groupsPhase?.status === 'finished' ? t('abgeschlossen') : t('{n} von {m} Spielen gewertet', { n: done, m: groupsPhase?.matches.length ?? 0 });

  if (!groupsPhase && !koPhase) {
    return (
      <div className="max-w-2xl">
        <SectionHead title={t('Tabelle')} />
        <Empty title={t('Noch keine Tabelle')}>
          {t('Sobald die')}{' '}
          <Link to="../vorrunde" className="text-blue underline">
            {t('Vorrunde')}
          </Link>{' '}
          {t('ausgelost ist, stehen hier die Tabellen.')}
        </Empty>
      </div>
    );
  }

  return (
    // `grid-cols-1` = minmax(0, 1fr): Sonst wächst die Spalte auf die Breite der
    // breitesten Tabelle, und am Handy schiebt sich die ganze Seite seitlich statt nur die Tabelle
    <div className="grid grid-cols-1 gap-6">
      {finalRanking.length > 0 && (
        <section>
          <SectionHead title={t('Endstand')} meta={t('aus der KO-Runde')} />
          <ol className="m-0 p-0 list-none grid grid-cols-[repeat(auto-fill,minmax(min(220px,100%),1fr))] gap-2">
            {finalRanking.map((r) => (
              <li key={r.teamId} className="bg-panel2 border border-line px-3 py-2 flex items-center gap-3">
                <span className="font-mono text-2xl text-red w-8 text-right">{r.rank}</span>
                <div className="min-w-0">
                  <div className="font-bold tracking-tight truncate">{lookup(r.teamId)?.name}</div>
                  <div className="text-xs text-dim truncate">
                    {lookup(r.teamId)
                      ?.players.map((p) => p.lastName)
                      .join(' · ')}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {clubMode && groupsPhase && groupsPhase.status !== 'planned' && (
        <>
          <section>
            <SectionHead title={t('Mannschaften')} meta={meta}>
              {!helfer && (
                <>
                  <Button variant="ghost" onClick={exportDay} title={t('Tabellen als Datei — für die Saison auf einem anderen Gerät (ohne Internet)')}>
                    <FileDown size={14} className="shrink-0" /> {t('Spieltag als Datei')}
                  </Button>
                  {/* Saison gibt es ab Pro (docs/21) — der Knopf bleibt sichtbar, mit Marke */}
                  <Gate feature="saison">
                    <Button
                      variant={hervorheben ? 'primary' : 'default'}
                      className={hervorheben ? 'saison-nudge' : undefined}
                      onClick={() => setSeasonOpen(true)}
                      data-saison-uebernehmen
                      data-hervorgehoben={hervorheben ? '1' : undefined}
                    >
                      <CalendarPlus size={14} className="shrink-0" /> {t('In Saison übernehmen')}
                    </Button>
                  </Gate>
                </>
              )}
            </SectionHead>
            {hervorheben && (
              <p className="m-0 mb-3 -mt-1 text-sm text-warn" data-saison-hinweis>
                {t('Spieltag fertig — jetzt in die Saison übernehmen, damit nichts verloren geht.')}
              </p>
            )}
            <Card>
              <div className="p-3 overflow-x-auto">
                <AggregateTable rows={clubStandings(comp, allRows)} nameLabel={t('Mannschaft')} highlightTop={1} />
              </div>
            </Card>
          </section>
          <section>
            <SectionHead title={t('Paare')} meta={t('jedes Paar gegen alle Paare der anderen Mannschaften')} />
            <Card>
              <div className="p-3 overflow-x-auto">
                <AggregateTable rows={teamAggregates(comp, allRows)} nameLabel={t('Paar')} highlightTop={3} />
              </div>
            </Card>
          </section>
          <section>
            <SectionHead title={t('Spieler')} meta={t('beide Spieler eines Paars erhalten dessen Ergebnisse — über die Saison zählt es einzeln')} />
            <Card>
              <div className="p-3 overflow-x-auto">
                <AggregateTable rows={playerStandings(comp, allRows)} nameLabel={t('Spieler')} highlightTop={3} />
              </div>
            </Card>
          </section>
        </>
      )}

      {!clubMode && groupsPhase && groupsPhase.status !== 'planned' && cfg && (
        <section>
          <SectionHead title={t('Gruppen')} meta={meta}>
            {!helfer && (
              <Gate feature="saison">
                <Button onClick={() => setSeasonOpen(true)} title={t('Tabellen dieses Spieltags in eine Saison übernehmen')}>
                  <CalendarPlus size={14} className="shrink-0" /> {t('In Saison übernehmen')}
                </Button>
              </Gate>
            )}
          </SectionHead>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(440px,100%),1fr))] gap-3">
            {tables.map(({ group, rows }) => (
              <Card key={group.id} title={t('Gruppe {g}', { g: group.name })}>
                <div className="p-3 overflow-x-auto">
                  <StandingsTable rows={rows} lookup={lookup} qualifiers={cfg.qualifiersPerGroup} pending={pending(group.id)} />
                </div>
              </Card>
            ))}
          </div>
          {bestRemaining.length > 0 && (
            <Card title={t('Beste {r}.-Platzierte ({n} qualifizieren sich)', { r: cfg.qualifiersPerGroup + 1, n: cfg.bestRemaining })} className="mt-3">
              <ol className="m-0 p-3 list-none grid gap-1 text-sm">
                {bestRemaining.map(({ candidate, notes }, i) => (
                  <li key={candidate.row.teamId} className={i < cfg.bestRemaining ? 'border-l-2 border-live pl-2' : 'pl-2.5 opacity-70'}>
                    <b>{lookup(candidate.row.teamId)?.name}</b>{' '}
                    <span className="text-dim">
                      {t('Gruppe {g}', { g: tables.find((t) => t.group.id === candidate.groupId)?.group.name ?? '' })} · {t('{n} Pkt', { n: candidate.row.points })} · {notes.join(' · ')}
                    </span>
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </section>
      )}
      {gesamt.length > 0 && !clubMode && (
        <section>
          <SectionHead title={t('Alle Teams')} meta={t('Punkte = Siege − Niederlagen, dann Siege, Niederlagen, Wurfdifferenz')} />
          <Card>
            <div className="p-3 overflow-x-auto">
              <GesamtTabelle rows={gesamt} lookup={lookup} hervor={3} />
            </div>
          </Card>
        </section>
      )}
      {groupsPhase?.status === 'planned' && <Empty title={t('Vorrunde noch nicht ausgelost')}>{t('Tabellen erscheinen nach der Auslosung.')}</Empty>}
      {seasonOpen && <SeasonImportDialog tournament={tournament} onClose={() => setSeasonOpen(false)} onDone={() => setInSaison(true)} />}
    </div>
  );
}
